import {
  Button,
  Container,
  Copy,
  Flex,
  Square,
  Stack,
  SvgCaretDown,
  SvgCaretUp,
  SvgCog,
} from '@grubhubprod/gh-cookbook-react/dist';
import React, { ReactNode } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { Dropdown } from 'src/components/Dropdown/Dropdown';
import Logo from 'src/components/enterprise/navigation/Logo/Logo';
import { MenuItem } from 'src/components/enterprise/navigation/NavMenu/MenuItem';
import { MenuSubItem } from 'src/components/enterprise/navigation/NavMenu/MenuSubItem';
import { getWindow } from 'src/utilities/window';
import { MultiCorpDropdown } from 'src/components/MultiCorpDropdown/MultiCorpDropdown';
import { InternalAdminMenuItems } from 'src/components/enterprise/navigation/NavMenu/InternalAdminMenuItems';
import { ParentAccountsMenuItems } from 'src/components/enterprise/navigation/NavMenu/ParentAccountsMenuItems';
import { ParentAdminLandingMenuItems } from 'src/components/enterprise/navigation/NavMenu/ParentAdminLandingMenuItems';
import { useCanAccessParentAdminFlows } from 'src/components/enterprise/Parents/Admin/hooks/useCanAccessParentAdminFlows/useCanAccessParentAdminFlows';
import { buildCAUIPath, CAUIPage, getHomePath } from 'src/constants/urls';
import { useReportingFeatureAccess } from 'src/components/enterprise/features/Reporting/Routes/useReportingFeatureAccess/useReportingFeatureAccess';

const NAV_HEIGHT = '57px';
const DESKTOP_NAV_MARGIN = '24px';

export interface INavigationProps {
  hideMenu?: boolean;
}

export interface IMenuItemsConfig {
  menu: string;
  hide?: boolean;
  subRoutes?: {
    text: string;
    path: string;
    hide?: boolean;
    useRouterLink?: boolean;
  }[];
  mainRoute?: string;
  useRouterLink?: boolean;
  icon?: ReactNode;
  /*
   * This property aligns the items to the right side.
   * default value: false.
   * */
  rightSide?: boolean;
  matchingPathnames?: string[];
  variant?: 'bold-and-highlight' | 'normal-no-highlight';
}

export interface IMenuConfig {
  menuConfig: IMenuItemsConfig[];
  internalAdminMenuConfig: IMenuItemsConfig[];
}

export default function Navigation({
  hideMenu = false,
}: INavigationProps): JSX.Element {
  const window = getWindow();
  const {
    user,
    corpData,
    enterpriseData: {
      isFinancialAdminUser,
      isInternalAdminUser,
      isInternalAdminPath,
      isParentAdminUser,
      isParentAdminPath,
    },
    allUserCorps,
  } = useAuth();

  const { corpid } = useParams<{ corpid: string }>();
  const { pathname } = useLocation();

  const { canAccessReporting } = useReportingFeatureAccess();

  const hasPremiumCorps = allUserCorps && allUserCorps.PREMIUM.length > 0;
  const hasMultiplePremiumCorps =
    allUserCorps && allUserCorps.PREMIUM.length > 1;
  const hasBasicCorps = allUserCorps && allUserCorps.BASIC.length > 0;
  const showCorpsDropdown =
    hasMultiplePremiumCorps || (hasBasicCorps && hasPremiumCorps);
  const orderHistoryEnabled = corpData?.orderHistoryEnabled;
  const groupOrderEnabled = corpData?.groupOrderEnabled;
  const showOrdersMenuItem = orderHistoryEnabled || groupOrderEnabled;
  const isCorpAdminUiProvider = corpData?.adminUiProvider === 'CORP_ADMIN';
  const canAccessParentAdminPages = useCanAccessParentAdminFlows();
  const showInvoicePaymentMenu =
    isInternalAdminUser || isFinancialAdminUser || isParentAdminUser;

  const shouldDisplayOrderCreationMenu =
    !isInternalAdminPath && !isParentAdminPath;

  const shouldDisplayMultiCorpDropdown =
    showCorpsDropdown &&
    !isInternalAdminPath &&
    !isInternalAdminUser &&
    !isParentAdminPath;

  const getRoutingConfig = (corpId: string): IMenuConfig => {
    return {
      menuConfig: [
        {
          menu: 'Employees & Groups',
          subRoutes: [
            {
              text: 'Employees',
              path: buildCAUIPath(CAUIPage.EMPLOYEES, { corpId }),
            },
            {
              text: 'Groups',
              useRouterLink: true,
              path: `/corporate/enterprise/${corpId}/groups`,
            },
          ],
        },

        {
          menu: 'Meal credit settings',
          subRoutes: [
            {
              text: 'Meal credits',
              useRouterLink: true,
              path: `/corporate/enterprise/${corpId}/meal-credits`,
            },
            {
              text: `${corpData?.expenseCodeAliasPlural}`,
              useRouterLink: false,
              path: buildCAUIPath(CAUIPage.EXPENSE_CODES, { corpId }),
            },
            {
              text: 'Expense Reasons',
              path: buildCAUIPath(CAUIPage.EXPENSE_REASONS, { corpId }),
            },
          ],
        },
        {
          menu: 'Orders',
          hide: !showOrdersMenuItem,
          subRoutes: [
            {
              text: 'Scheduled team orders',
              useRouterLink: false,
              path: buildCAUIPath(CAUIPage.GROUP_ORDERS, { corpId }),
              hide: !groupOrderEnabled,
            },
            {
              text: 'Order History',
              useRouterLink: true,
              path: `/corporate/enterprise/${corpId}/order-history`,
            },
          ],
        },
        {
          menu: 'Billing',
          subRoutes: [
            {
              text: 'Invoices',
              useRouterLink: true,
              path: `/corporate/enterprise/${corpId}/invoices`,
            },
            {
              text: 'Invoice recipients',
              useRouterLink: true,
              path: `/corporate/enterprise/${corpId}/invoice-notifications`,
            },
            {
              text: 'Invoice payment methods',
              useRouterLink: true,
              path: `/corporate/enterprise/${corpId}/payments`,
              hide: !(
                showInvoicePaymentMenu &&
                corpData?.invoicePaymentMethod === 'CREDIT_CARD_PRIMARY'
              ),
            },
          ],
        },

        {
          menu: 'Company details',
          subRoutes: [
            {
              text: 'Ordering policy',
              path: buildCAUIPath(CAUIPage.ORDERING_POLICY, { corpId }),
              hide: !(corpData?.orderPolicyId && isCorpAdminUiProvider),
            },
            {
              text: 'Locations',
              useRouterLink: true,
              path: `/corporate/enterprise/${corpId}/locations`,
            },
            {
              text: 'Departments',
              useRouterLink: true,
              path: `/corporate/enterprise/${corpId}/departments`,
            },
            {
              text: 'Tax exemptions',
              useRouterLink: true,
              path: `/corporate/enterprise/${corpId}/tax-exemptions`,
              hide: !corpData?.taxExemptEnabled,
            },
          ],
        },

        {
          menu: 'Reporting',
          hide: !canAccessReporting,
          subRoutes: [
            {
              text: 'Create Reports',
              useRouterLink: true,
              path: `/corporate/enterprise/${corpId}/reports/create`,
            },
            {
              text: 'Manage Reports',
              useRouterLink: true,
              path: `/corporate/enterprise/${corpId}/reports`,
            },
          ],
        },
      ],

      internalAdminMenuConfig: [
        {
          menu: 'Parent accounts',
          subRoutes: [],
          mainRoute: '/groupadmin/parents',
          matchingPathnames: ['/corporate/enterprise/parents'],
        },
        {
          menu: 'Corporate accounts',
          subRoutes: [],
          mainRoute: '/groupadmin/corps',
        },
        {
          menu: 'Administrators',
          subRoutes: [],
          mainRoute: buildCAUIPath(CAUIPage.ADMINS),
        },
        {
          menu: 'Settings',
          subRoutes: [],
          mainRoute: `/corporate/enterprise/toggles`,
          rightSide: true,
          icon: <SvgCog size="24px" />,
        },
      ],
    };
  };

  function addUtmParams(orderType: string): string {
    return (
      `utm_source=${user.ud_id.toLowerCase()}_corpadmin&` +
      'utm_medium=content_owned&' +
      `utm_campaign=corpadmin_createneworder&` +
      `utm_content=${orderType}`
    );
  }

  return (
    <nav data-testid="navbar" className="navBarFixed">
      <Flex bg="globals.white" flexDirection="column">
        <Flex
          alignItems="center"
          justifyContent="space-between"
          bg="globals.white"
          style={{
            height: NAV_HEIGHT,
          }}
        >
          <Square
            ml={DESKTOP_NAV_MARGIN}
            as="header"
            backgroundColor="globals.white"
          >
            <Logo />
          </Square>

          <Flex alignItems="center" mr={DESKTOP_NAV_MARGIN}>
            {shouldDisplayOrderCreationMenu && renderCreateOrdersDropdown()}

            {shouldDisplayMultiCorpDropdown && (
              <MultiCorpDropdown
                hasBasicCorps={hasBasicCorps}
                corpId={corpid as string}
                hasPremiumCorps={hasPremiumCorps}
                allUserCorps={allUserCorps}
              />
            )}

            {!isInternalAdminPath && isInternalAdminUser && (
              <Button
                variant="tertiary"
                buttonSize="small"
                onClick={() => {
                  window.location.href = '/groupadmin/corps';
                }}
              >
                <Stack as="span" x="xxx-sm">
                  <span>Client Setup Home</span>
                </Stack>
              </Button>
            )}
          </Flex>
        </Flex>

        <Flex
          bg="globals.white"
          justifyContent="flex-end"
          alignItems="center"
          className="navBarBottom"
          borderTop="1px solid"
          borderTopColor="lightLine"
          width="100%"
          style={{
            height: NAV_HEIGHT,
          }}
        >
          <Container variant="default" className="navContainer">
            <Flex>{renderMenuItems()}</Flex>
          </Container>
        </Flex>
      </Flex>
    </nav>
  );

  function renderMenuItems(): JSX.Element {
    const ENTERPRISE_ROUTES = getRoutingConfig(corpid as string);
    if (hideMenu) {
      return null;
    }

    const isParentAdminLandingPages = [
      '/corporate/enterprise/parents/accounts',
      '/corporate/enterprise/corporations',
    ].includes(pathname);

    if (canAccessParentAdminPages && isParentAdminLandingPages) {
      return <ParentAdminLandingMenuItems />;
    }

    if (isInternalAdminPath) {
      return (
        <InternalAdminMenuItems
          menuItems={ENTERPRISE_ROUTES.internalAdminMenuConfig}
        />
      );
    }

    const isParentAccountsPath =
      pathname.indexOf('/corporate/enterprise/parents') !== -1;

    if (canAccessParentAdminPages && isParentAccountsPath) {
      return <ParentAccountsMenuItems />;
    }

    return (
      <>
        <MenuItem
          menu="Home"
          subRoutes={[]}
          mainRoute={getHomePath(corpid)}
          useRouterLink
        />
        {ENTERPRISE_ROUTES.menuConfig.map((config) => (
          <MenuItem
            key={config.menu}
            menu={config.menu}
            hide={config.hide}
            subRoutes={config.subRoutes}
          />
        ))}
      </>
    );
  }

  function renderCreateOrdersDropdown(): JSX.Element {
    return (
      <Dropdown
        toggle={({ expanded }) => (
          <Button variant="tertiary" buttonSize="small">
            <Stack as="span" x="xxx-sm">
              <span>Create a new order</span>
            </Stack>
            <Copy variant="body" color="interactive">
              {expanded ? (
                <SvgCaretUp size="16px" />
              ) : (
                <SvgCaretDown size="16px" />
              )}
            </Copy>
          </Button>
        )}
        menu={({ show }) =>
          show && (
            <Flex
              position="relative"
              flexDirection="column"
              width="200px"
              dimension="1"
              zIndex={1031}
            >
              {groupOrderEnabled && (
                <MenuSubItem
                  text="Scheduled team order"
                  href={buildCAUIPath(CAUIPage.GROUP_ORDERS_CREATE, {
                    corpId: corpid,
                  })}
                  copyColor="secondaryText"
                  copyVariant="caption"
                />
              )}
              <MenuSubItem
                text="Catering order"
                href={`${
                  window.location.origin
                }/search?facet=order_type%3Acatering&${addUtmParams(
                  'catering'
                )}`}
                copyColor="secondaryText"
                copyVariant="caption"
              />
              <MenuSubItem
                text="Regular order"
                href={`${window.location.origin}/search?${addUtmParams(
                  'regular'
                )}`}
                copyColor="secondaryText"
                copyVariant="caption"
              />
            </Flex>
          )
        }
      />
    );
  }
}
